import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { pick } from '../../util/common';
import { storableError } from '../../util/errors';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { validateShipStationCredentials } from '../../util/api';

const getErrorMessage = error => {
  if (!error) {
    return null;
  }

  if (typeof error === 'string') {
    return error;
  }

  const apiErrorMessage = error.apiErrors?.[0]?.title || error.apiErrors?.[0]?.detail;
  return apiErrorMessage || error.message || error.statusText || 'Something went wrong';
};

// ================ Async thunks ================ //

/**
 * Load ShipStation credentials for the current user
 * Note: API secret is never returned, only the API key
 */
const loadShipStationCredentialsPayloadCreator = (
  _params,
  { dispatch, getState, extra: sdk, rejectWithValue }
) => {
  const state = getState();
  const currentUser = state.user.currentUser;

  if (!currentUser || !currentUser.id) {
    return rejectWithValue('User not authenticated');
  }

  // ShipStation credentials are stored in currentUser.attributes.profile.privateData
  const privateData = currentUser.attributes?.profile?.privateData || {};
  const hasCredentials = !!privateData.shipStationApiKey;

  return Promise.resolve({
    apiKey: privateData.shipStationApiKey || '',
    shipFromName: privateData.shipFromName || '',
    shipFromAddressLine1: privateData.shipFromAddressLine1 || '',
    shipFromAddressLine2: privateData.shipFromAddressLine2 || '',
    shipFromCity: privateData.shipFromCity || '',
    shipFromState: privateData.shipFromState || '',
    shipFromPostalCode: privateData.shipFromPostalCode || '',
    shipFromCountry: privateData.shipFromCountry || 'US',
    hasCredentials,
  });
};

export const loadShipStationCredentialsThunk = createAsyncThunk(
  'ShipStationSettingsPage/loadCredentials',
  loadShipStationCredentialsPayloadCreator
);

// Backward compatible wrapper function
export const loadShipStationCredentials = () => dispatch => {
  return dispatch(loadShipStationCredentialsThunk()).unwrap();
};

/**
 * Test ShipStation API connection with provided credentials
 */
const testShipStationConnectionPayloadCreator = ({ apiKey }, { rejectWithValue }) => {
  return validateShipStationCredentials({ apiKey })
    .then(response => {
      if (response.valid) {
        return { valid: true };
      } else {
        return rejectWithValue(response.error || 'Invalid credentials');
      }
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const testShipStationConnectionThunk = createAsyncThunk(
  'ShipStationSettingsPage/testConnection',
  testShipStationConnectionPayloadCreator
);

// Backward compatible wrapper function
export const testShipStationConnection = params => dispatch => {
  return dispatch(testShipStationConnectionThunk(params)).unwrap();
};

/**
 * Save ShipStation credentials to user's privateData
 */
const saveShipStationCredentialsPayloadCreator = (
  { apiKey },
  { dispatch, extra: sdk, rejectWithValue }
) => {
  const privateData = {
    shipStationApiKey: apiKey,
  };

  return sdk.currentUser
    .updateProfile(
      { privateData },
      {
        expand: true,
        include: ['profileImage'],
        'fields.image': ['variants.square-small', 'variants.square-small2x'],
      }
    )
    .then(response => {
      const currentUser = response.data.data;
      dispatch(fetchCurrentUser({ updateHasListings: false, updateNotifications: false }));
      return currentUser;
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const saveShipStationCredentialsThunk = createAsyncThunk(
  'ShipStationSettingsPage/saveCredentials',
  saveShipStationCredentialsPayloadCreator
);

// Backward compatible wrapper function
export const saveShipStationCredentials = params => dispatch => {
  return dispatch(saveShipStationCredentialsThunk(params)).unwrap();
};

/**
 * Remove ShipStation credentials from user's privateData
 */
const removeShipStationCredentialsPayloadCreator = (
  _params,
  { dispatch, extra: sdk, rejectWithValue }
) => {
  const privateData = {
    shipStationApiKey: null,
  };

  return sdk.currentUser
    .updateProfile(
      { privateData },
      {
        expand: true,
        include: ['profileImage'],
        'fields.image': ['variants.square-small', 'variants.square-small2x'],
      }
    )
    .then(response => {
      const currentUser = response.data.data;
      dispatch(fetchCurrentUser({ updateHasListings: false, updateNotifications: false }));
      return currentUser;
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const removeShipStationCredentialsThunk = createAsyncThunk(
  'ShipStationSettingsPage/removeCredentials',
  removeShipStationCredentialsPayloadCreator
);

// Backward compatible wrapper function
export const removeShipStationCredentials = () => dispatch => {
  return dispatch(removeShipStationCredentialsThunk()).unwrap();
};

/**
 * Save ship-from address to user's privateData (separate from API key)
 */
const saveShipFromAddressPayloadCreator = (
  { shipFromName, shipFromAddressLine1, shipFromAddressLine2, shipFromCity, shipFromState, shipFromPostalCode, shipFromCountry },
  { dispatch, extra: sdk, rejectWithValue }
) => {
  const privateData = {
    shipFromName: shipFromName || '',
    shipFromAddressLine1: shipFromAddressLine1 || '',
    shipFromAddressLine2: shipFromAddressLine2 || '',
    shipFromCity: shipFromCity || '',
    shipFromState: shipFromState || '',
    shipFromPostalCode: shipFromPostalCode || '',
    shipFromCountry: shipFromCountry || 'US',
  };

  return sdk.currentUser
    .updateProfile(
      { privateData },
      {
        expand: true,
        include: ['profileImage'],
        'fields.image': ['variants.square-small', 'variants.square-small2x'],
      }
    )
    .then(response => {
      const currentUser = response.data.data;
      dispatch(fetchCurrentUser({ updateHasListings: false, updateNotifications: false }));
      return currentUser;
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const saveShipFromAddressThunk = createAsyncThunk(
  'ShipStationSettingsPage/saveShipFromAddress',
  saveShipFromAddressPayloadCreator
);

export const saveShipFromAddress = params => dispatch => {
  return dispatch(saveShipFromAddressThunk(params)).unwrap();
};

// ================ Slice ================ //

const initialState = {
  saveInProgress: false,
  saveError: null,
  credentialsSaved: false,
  testInProgress: false,
  testError: null,
  testSuccess: false,
  removeInProgress: false,
  removeError: null,
  credentialsRemoved: false,
  shipFromSaveInProgress: false,
  shipFromSaveError: null,
  shipFromAddressSaved: false,
};

const shipStationSettingsPageSlice = createSlice({
  name: 'ShipStationSettingsPage',
  initialState,
  reducers: {
    setInitialValues: (state, action) => {
      return { ...initialState, ...pick(action.payload, Object.keys(initialState)) };
    },
    clearTestResults: state => {
      state.testSuccess = false;
      state.testError = null;
    },
    clearSaveResults: state => {
      state.credentialsSaved = false;
      state.saveError = null;
    },
    clearShipFromSaveResults: state => {
      state.shipFromAddressSaved = false;
      state.shipFromSaveError = null;
    },
  },
  extraReducers: builder => {
    builder
      // Test Connection cases
      .addCase(testShipStationConnectionThunk.pending, state => {
        state.testInProgress = true;
        state.testError = null;
        state.testSuccess = false;
      })
      .addCase(testShipStationConnectionThunk.fulfilled, state => {
        state.testInProgress = false;
        state.testSuccess = true;
        state.testError = null;
      })
      .addCase(testShipStationConnectionThunk.rejected, (state, action) => {
        state.testInProgress = false;
        state.testSuccess = false;
        state.testError = getErrorMessage(action.payload || action.error);
      })

      // Save Credentials cases
      .addCase(saveShipStationCredentialsThunk.pending, state => {
        state.saveInProgress = true;
        state.saveError = null;
        state.credentialsSaved = false;
      })
      .addCase(saveShipStationCredentialsThunk.fulfilled, state => {
        state.saveInProgress = false;
        state.credentialsSaved = true;
        state.saveError = null;
      })
      .addCase(saveShipStationCredentialsThunk.rejected, (state, action) => {
        state.saveInProgress = false;
        state.credentialsSaved = false;
        state.saveError = action.payload;
      })

      // Remove Credentials cases
      .addCase(removeShipStationCredentialsThunk.pending, state => {
        state.removeInProgress = true;
        state.removeError = null;
        state.credentialsRemoved = false;
      })
      .addCase(removeShipStationCredentialsThunk.fulfilled, state => {
        state.removeInProgress = false;
        state.credentialsRemoved = true;
        state.removeError = null;
        state.testSuccess = false;
        state.testError = null;
      })
      .addCase(removeShipStationCredentialsThunk.rejected, (state, action) => {
        state.removeInProgress = false;
        state.credentialsRemoved = false;
        state.removeError = action.payload;
      })

      // Save Ship From Address cases
      .addCase(saveShipFromAddressThunk.pending, state => {
        state.shipFromSaveInProgress = true;
        state.shipFromSaveError = null;
        state.shipFromAddressSaved = false;
      })
      .addCase(saveShipFromAddressThunk.fulfilled, state => {
        state.shipFromSaveInProgress = false;
        state.shipFromAddressSaved = true;
        state.shipFromSaveError = null;
      })
      .addCase(saveShipFromAddressThunk.rejected, (state, action) => {
        state.shipFromSaveInProgress = false;
        state.shipFromAddressSaved = false;
        state.shipFromSaveError = action.payload;
      });
  },
});

// Export the action creators
export const { setInitialValues, clearTestResults, clearSaveResults, clearShipFromSaveResults } =
  shipStationSettingsPageSlice.actions;

export default shipStationSettingsPageSlice.reducer;

// ================ Load Data ================ //

export const loadData = () => (dispatch, getState, sdk) => {
  // Clear state so that previously loaded data is not visible
  // in case this page load fails.
  dispatch(setInitialValues({}));

  const fetchCurrentUserOptions = {
    updateHasListings: false,
    updateNotifications: false,
  };

  return dispatch(fetchCurrentUser(fetchCurrentUserOptions)).then(response => {
    // Load credentials from current user's privateData
    return dispatch(loadShipStationCredentials());
  });
};
